import { Vec3 } from '@/core/math/Vec3';
import { TerrainParams, elevationAt } from './TerrainPipeline';

/**
 * CPU-side terrain sampler used for collision, placement and ground queries.
 *
 * This is the same pure function the mesh workers call, which is the point:
 * the surface the player collides with is generated by identical code to the
 * surface they see, so there is no possibility of the collider drifting away
 * from the visual mesh.
 *
 * The cache exists because contact resolution samples the same few square
 * metres many times per second (four landing legs, a character capsule, a
 * vehicle's wheels), and terrain evaluation is the single most expensive pure
 * function in the project.
 */
export class HeightField {
  private cache = new Map<string, number>();
  private cacheOrder: string[] = [];

  /** Sample spacing used for ground queries, metres. Matches the finest LOD. */
  querySpacing = 2;
  /** Maximum cached samples before the oldest are evicted. */
  cacheLimit = 20000;
  /** Quantisation of cached queries, metres. */
  cacheResolution = 0.75;

  constructor(public params: TerrainParams) {}

  get radius(): number {
    return this.params.radius;
  }

  /** Highest possible terrain above the datum, metres. */
  get ceiling(): number {
    return this.params.profile.maxElevation * 1.35;
  }

  /**
   * Lowest possible terrain below the datum, metres (negative).
   *
   * Horizon culling uses this as the occluding sphere's radius. Deliberately
   * pessimistic: assuming the planet is no larger than its deepest basin means
   * the test can only ever cull something that is genuinely hidden, at the cost
   * of keeping a few chunks that are not.
   */
  get floor(): number {
    return -this.params.profile.maxElevation * 1.35;
  }

  /** Elevation above the datum in a planet-fixed direction, metres. */
  elevationInDirection(dx: number, dy: number, dz: number): number {
    const q = this.cacheResolution / this.params.radius;
    const kx = Math.round(dx / q);
    const ky = Math.round(dy / q);
    const kz = Math.round(dz / q);
    const key = `${kx},${ky},${kz}`;
    const hit = this.cache.get(key);
    if (hit !== undefined) return hit;

    const len = Math.hypot(dx, dy, dz) || 1;
    const value = elevationAt(dx / len, dy / len, dz / len, this.params, this.querySpacing);

    this.cache.set(key, value);
    this.cacheOrder.push(key);
    if (this.cacheOrder.length > this.cacheLimit) {
      // Drop the oldest quarter in one pass rather than one at a time.
      const drop = this.cacheOrder.splice(0, this.cacheLimit >> 2);
      for (const k of drop) this.cache.delete(k);
    }
    return value;
  }

  /** Elevation at a planet-fixed position (direction is taken from it). */
  elevationAtPosition(position: Vec3): number {
    return this.elevationInDirection(position.x, position.y, position.z);
  }

  /** Surface radius (datum + elevation) at a planet-fixed position, metres. */
  surfaceRadiusAt(position: Vec3): number {
    return this.params.radius + this.elevationAtPosition(position);
  }

  /**
   * Radius of the *walkable* surface: the sea surface over ocean, the ground
   * elsewhere. Landing gear and characters use this so a vessel does not sink
   * to the sea floor.
   */
  walkableRadiusAt(position: Vec3): number {
    const e = this.elevationAtPosition(position);
    if (this.params.seaLevel !== Number.NEGATIVE_INFINITY && e < this.params.seaLevel) {
      return this.params.radius + this.params.seaLevel;
    }
    return this.params.radius + e;
  }

  /** Height of a planet-fixed position above the local surface, metres. */
  altitudeAt(position: Vec3): number {
    return position.length() - this.walkableRadiusAt(position);
  }

  /** True when the position is over water. */
  isOverWater(position: Vec3): boolean {
    if (this.params.seaLevel === Number.NEGATIVE_INFINITY) return false;
    return this.elevationAtPosition(position) < this.params.seaLevel;
  }

  /**
   * Surface normal at a planet-fixed position, computed by central differences
   * on the tangent plane.
   */
  normalAt(position: Vec3, spacing = this.querySpacing * 2, out = new Vec3()): Vec3 {
    const up = position.normalized();
    let east = Vec3.crossVectors(new Vec3(0, 1, 0), up);
    if (east.lengthSq() < 1e-12) east = Vec3.anyPerpendicular(up);
    east.normalize();
    const north = Vec3.crossVectors(up, east).normalize();

    const r = this.params.radius;
    const d = spacing / r;

    const sample = (offsetA: Vec3, offsetB: Vec3, sa: number, sb: number): Vec3 => {
      const dir = up.clone().addScaled(offsetA, sa * d).addScaled(offsetB, sb * d).normalize();
      const rad = this.params.radius + this.elevationInDirection(dir.x, dir.y, dir.z);
      return dir.mulScalar(rad);
    };

    const pE = sample(east, north, 1, 0);
    const pW = sample(east, north, -1, 0);
    const pN = sample(east, north, 0, 1);
    const pS = sample(east, north, 0, -1);

    const a = Vec3.subVectors(pE, pW);
    const b = Vec3.subVectors(pN, pS);
    Vec3.crossVectors(a, b, out).normalize();
    if (out.dot(up) < 0) out.negate();
    return out;
  }

  /** Slope in radians at a planet-fixed position. */
  slopeAt(position: Vec3, spacing = this.querySpacing * 2): number {
    const n = this.normalAt(position, spacing);
    const up = position.normalized();
    return Math.acos(Math.min(1, Math.max(-1, n.dot(up))));
  }

  clearCache(): void {
    this.cache.clear();
    this.cacheOrder.length = 0;
  }

  get cacheSize(): number {
    return this.cache.size;
  }
}
